import { useQuery } from "@tanstack/react-query";
import {
  ShieldCheck,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Users,
  FileWarning,
  Bell,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import { supabase } from "@/integrations/supabase/client";
import { format, differenceInDays } from "date-fns";

const statusColors: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  pending_onboarding: "bg-blue-100 text-blue-700",
  pending_verification: "bg-amber-100 text-amber-700",
  expired_license: "bg-red-100 text-red-700",
  suspended: "bg-red-100 text-red-700",
  inactive: "bg-muted text-muted-foreground",
};

const statusLabels: Record<string, string> = {
  active: "Active",
  pending_onboarding: "Pending Onboarding",
  pending_verification: "Pending Verification",
  expired_license: "Expired License",
  suspended: "Suspended",
  inactive: "Inactive",
};

export default function ComplianceDashboard() {
  const { data: complianceData, isLoading } = useQuery({
    queryKey: ["compliance-dashboard"],
    queryFn: async () => {
      const [complianceRes, licensesRes, alertsRes, providersRes] = await Promise.all([
        supabase.from("provider_compliance").select("*"),
        supabase.from("provider_licenses").select("*"),
        supabase.from("compliance_alerts").select("*").eq("is_resolved", false),
        supabase.from("providers").select("id, display_name, specialty"),
      ]);

      return {
        compliance: complianceRes.data || [],
        licenses: licensesRes.data || [],
        alerts: alertsRes.data || [],
        providers: providersRes.data || [],
      };
    },
  });

  const compliance = complianceData?.compliance || [];
  const licenses = complianceData?.licenses || [];
  const alerts = complianceData?.alerts || [];
  const providers = complianceData?.providers || [];

  // Status distribution
  const statusCounts = compliance.reduce<Record<string, number>>((acc, c) => {
    acc[c.overall_status] = (acc[c.overall_status] || 0) + 1;
    return acc;
  }, {});

  const totalProviders = compliance.length;

  // Expiring licenses (within 90 days)
  const today = new Date();
  const expiringLicenses = licenses
    .filter((l) => {
      if (!l.expiration_date) return false;
      const days = differenceInDays(new Date(l.expiration_date), today);
      return days >= 0 && days <= 90;
    })
    .sort((a, b) => new Date(a.expiration_date!).getTime() - new Date(b.expiration_date!).getTime());

  const getProviderName = (complianceId: string) => {
    const comp = compliance.find((c) => c.id === complianceId);
    if (!comp) return "Unknown";
    const prov = providers.find((p) => p.id === comp.provider_id);
    return prov?.display_name || "Unknown";
  };

  const getDaysUntilExpiry = (date: string) => {
    return differenceInDays(new Date(date), today);
  };

  const getExpiryBadge = (days: number) => {
    if (days <= 30) return <Badge className="bg-red-100 text-red-700">{days}d</Badge>;
    if (days <= 60) return <Badge className="bg-amber-100 text-amber-700">{days}d</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-700">{days}d</Badge>;
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-bold text-foreground">Provider Compliance Dashboard</h1>
              <p className="text-muted-foreground">
                Monitor provider credentials, licenses, and compliance status
              </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Providers</p>
                      <p className="text-2xl font-bold">{totalProviders}</p>
                      <p className="text-sm text-emerald-600 mt-1">
                        {statusCounts["active"] || 0} active
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Onboarding</p>
                      <p className="text-2xl font-bold">{statusCounts["pending_onboarding"] || 0}</p>
                      <p className="text-sm text-blue-600 mt-1">
                        {statusCounts["pending_verification"] || 0} pending verification
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Clock className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Expiring Licenses</p>
                      <p className="text-2xl font-bold">{expiringLicenses.length}</p>
                      <p className="text-sm text-amber-600 mt-1">Within 90 days</p>
                    </div>
                    <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <FileWarning className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active Alerts</p>
                      <p className="text-2xl font-bold">{alerts.length}</p>
                      <p className="text-sm text-red-600 mt-1">
                        {alerts.filter((a) => a.severity === "critical").length} critical
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Bell className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Compliance Status Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Compliance Status Distribution</CardTitle>
                  <CardDescription>Overview of all provider statuses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {totalProviders === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No providers enrolled yet</p>
                      <p className="text-sm">Add providers from the Provider Database page</p>
                    </div>
                  ) : (
                    Object.entries(statusCounts).map(([status, count]) => (
                      <div key={status} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Badge className={statusColors[status] || "bg-muted"}>
                              {statusLabels[status] || status}
                            </Badge>
                          </div>
                          <span className="font-medium">
                            {count} ({((count / totalProviders) * 100).toFixed(0)}%)
                          </span>
                        </div>
                        <Progress value={(count / totalProviders) * 100} className="h-2" />
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Expiring Licenses */}
              <Card>
                <CardHeader>
                  <CardTitle>Expiring Licenses</CardTitle>
                  <CardDescription>Licenses expiring within the next 90 days</CardDescription>
                </CardHeader>
                <CardContent>
                  {expiringLicenses.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No licenses expiring soon</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {expiringLicenses.slice(0, 8).map((license) => {
                        const days = getDaysUntilExpiry(license.expiration_date!);
                        return (
                          <div
                            key={license.id}
                            className="flex items-center justify-between p-3 rounded-lg border"
                          >
                            <div>
                              <p className="font-medium text-sm">
                                {getProviderName(license.compliance_id)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {license.state} — #{license.license_number}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(license.expiration_date!), "MMM d, yyyy")}
                              </span>
                              {getExpiryBadge(days)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Alerts */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Compliance Alerts</CardTitle>
                <CardDescription>Unresolved alerts requiring attention</CardDescription>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No active alerts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.slice(0, 10).map((alert) => (
                      <div
                        key={alert.id}
                        className="flex items-start gap-3 p-3 rounded-lg border"
                      >
                        {alert.severity === "critical" ? (
                          <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{alert.title}</p>
                          <p className="text-xs text-muted-foreground">{alert.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(new Date(alert.created_at), "MMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                        <Badge
                          className={
                            alert.severity === "critical"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                          }
                        >
                          {alert.severity}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
